/**
 * The offline write queue, for both clients.
 *
 * `mobile/README.md` called this the part worth building carefully:
 *
 *   > people log food in kitchens, gyms and restaurants, and an entry that
 *   > silently fails to save is the worst bug this app can have.
 *
 * That is no less true in a browser. The web client used to write straight
 * through, which was survivable while logging was one request at a time and
 * became untenable the moment a screen could issue three or four in a row: the
 * failure mode is a basket half-committed, with no record of which half.
 *
 * The *rules* — what collapses into what, what order things replay in, what is
 * dropped rather than retried forever — have always lived in `queue.ts`, tested
 * without a device. This is the machinery around them, and it lives here rather
 * than in either app because the two clients disagreeing about when a write is
 * safe would be a bug, not a platform difference.
 *
 * ## What each platform still owns
 *
 * Two things, injected, because they are the only genuinely different parts:
 *
 * - **`storage`** — `AsyncStorage` on the phone, `localStorage` in a browser.
 * - **`onWake`** — the moments worth retrying on. Foregrounding on the phone;
 *   `online` and a tab becoming visible on the web. Both answer the same
 *   question ("has the network situation plausibly changed?") and neither
 *   platform can answer it the other's way.
 *
 * ## Every write goes through the queue, online or not
 *
 * There is deliberately no "try the network, fall back to the queue" branch.
 * One path means the offline code runs on every single log rather than only
 * when it is already too late to discover it is broken, and the entry appears
 * instantly either way because the optimistic row is written before anything is
 * attempted. The cost — a permanent failure surfacing after the fact rather
 * than at the call site — is paid explicitly: those are counted and shown.
 */

import { useQueryClient, type QueryClient } from "@tanstack/react-query";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";

import { instantOn } from "./day";
import { applyQueued, drainQueue, enqueue, optimisticEntry, type QueuedWrite } from "./queue";
import { forGrams } from "./macros";
import { forGramsNutrients, NO_NUTRIENTS, type NutrientsPer100 } from "./nutrients";
import { keys } from "./queries";
import type { ApiClient } from "./scan";
import type { DaySummary, FoodLog, LogInput, Meal, Profile } from "./types";

/** Retry backoff while writes are stuck, in ms. Capped so a client that regains
 *  the network in a lift does not sit on a ten-minute timer. */
const RETRY_MS = [2_000, 5_000, 15_000, 30_000, 60_000];

/**
 * Where the queue rests between launches.
 *
 * Async on both sides even though `localStorage` is synchronous, because the
 * phone's is not and one interface beats two. Implementations must swallow
 * their own failures: unreadable storage loses the queue, and taking the app
 * down with it loses everything else too.
 */
export interface QueueStorage {
  load(): Promise<QueuedWrite[]>;
  save(queue: readonly QueuedWrite[]): Promise<void>;
}

/**
 * Take a portion out of the cached fridge, without waiting for the server.
 *
 * A negative count puts one back, which is what an undo does before the write
 * it is undoing has ever left the device.
 *
 * Floored at nought and mirrored on both shapes the pot is cached in — the rail
 * of open pots, and the one pot somebody may have opened. They are the same
 * number, and a sheet showing "1 left" over a rail showing "2 left" is a bug
 * the user reads as the app having lost the write.
 *
 * Not a delete when it reaches nought: whether an empty pot leaves the rail is
 * the server's rule, and it is applied on the next fetch. Guessing at it here
 * would make the tile vanish and come back.
 */
function spendPortion(client: QueryClient, batchId: string, portions: number): void {
  type Pot = { id: string; portions_remaining: number; portions_made: number };
  // Clamped at both ends, the same way the server clamps: a pot cannot hold
  // less than nothing, and an undo cannot make it fuller than it was cooked.
  const spend = <T extends Pot>(row: T): T =>
    row.id === batchId
      ? {
          ...row,
          portions_remaining: Math.min(
            row.portions_made,
            Math.max(0, row.portions_remaining - portions),
          ),
        }
      : row;

  client.setQueryData<Pot[]>(keys.batches, (rows) => rows?.map(spend));
  client.setQueryData<Pot>(keys.batch(batchId), (row) => (row ? spend(row) : row));
}

/**
 * Rewrite one cached day, then recompute its totals from its own entries.
 *
 * Recomputed rather than adjusted by a delta, because a delta has to be right
 * four times per operation and gets it wrong once per edge case — a quick-add
 * with no weight, an entry whose macros were corrected, a delete that races a
 * refetch. Summing what is actually there cannot drift.
 *
 * This is also what makes the day's figure include the queue, which the design
 * requires and states on screen ("Includes the queued entries."). A total that
 * counted only what had reached the server would fall as soon as you went
 * offline, which reads as food going missing.
 */
function patchDay(
  client: QueryClient,
  localDate: string,
  change: (entries: FoodLog[]) => FoodLog[],
): void {
  client.setQueryData<DaySummary>(keys.day(localDate), (day) => {
    if (!day) return day;
    const entries = change(day.entries);
    const totals = entries.reduce(
      (sum, entry) => ({
        kcal: sum.kcal + entry.kcal,
        protein_g: sum.protein_g + entry.protein_g,
        carbs_g: sum.carbs_g + entry.carbs_g,
        fat_g: sum.fat_g + entry.fat_g,
      }),
      { kcal: 0, protein_g: 0, carbs_g: 0, fat_g: 0 },
    );
    const target = day.target;
    return {
      ...day,
      entries,
      totals,
      remaining: target
        ? {
            kcal: target.kcal - totals.kcal,
            protein_g: target.protein_g - totals.protein_g,
            carbs_g: target.carbs_g - totals.carbs_g,
            fat_g: target.fat_g - totals.fat_g,
          }
        : null,
    };
  });
}

export interface Offline {
  /** Writes still waiting for the network. */
  pending: number;
  /**
   * The ids of entries still queued.
   *
   * A count alone cannot answer "is *this* row still waiting?", which is what a
   * diary needs in order to mark one — and marking the row is what turns "three
   * entries waiting" from an alarm into information. The ids are the optimistic
   * rows' own ids, so a caller compares against `entry.id` directly.
   */
  pendingIds: ReadonlySet<string>;
  /** Writes the server refused outright, since the last time they were seen. */
  rejected: number;
  acknowledgeRejected(): void;
  /** Try the queue now — the button behind "Retry everything unsent". */
  retry(): void;
  /**
   * Log a food, and name what was written.
   *
   * The returned id is the queued row's own — the client id, which is also the
   * optimistic entry's id until the server issues one. It is returned because
   * the caller often has to talk about the write afterwards: the add sheet's
   * undo tray takes back the last thing it wrote, and "the last thing" is not
   * something it can find by searching the day for a matching weight.
   */
  logFood(
    input: LogInput,
    optimistic: {
      name: string;
      meal: Meal;
      quantityG: number;
      /**
       * The food's own figures. The four optional nutrients ride along so the
       * queued row carries them too — a phone that logs them as unknown for a
       * food that declares them writes that gap into `food_logs` permanently,
       * and no later release can repair it.
       */
      per100: {
        kcal: number;
        protein_g: number;
        carbs_g: number;
        fat_g: number;
      } & NutrientsPer100;
      localDate: string;
    },
  ): string;
  /**
   * Eat one portion out of a pot, and take it out of the fridge.
   *
   * Through the queue like every other tap in the sheet, so the row appears at
   * once, survives a dead connection, and can be undone from the tray. Returns
   * the queued row's id for the same reason `logFood` does.
   *
   * The caller supplies the figures because only it knows them: the pot's
   * per-portion macros are already on screen beside the tile that was tapped,
   * and re-deriving them here would be a second copy of an arithmetic that has
   * to agree with what the user just read.
   */
  logPortion(
    input: {
      batchId: string;
      meal: Meal;
      /**
       * What the pot is charged, always — two decimals, from `helpingFor`.
       * Drives the on-screen decrement and the undo, whichever way the body
       * says how much.
       */
      portions: number;
      /** The plate on the scales. When set, the body says grams and the
       *  server derives the portions; `portions` above is the same figure,
       *  worked out on the client so the tile moves before the write drains. */
      quantity_g?: number;
    },
    optimistic: {
      name: string;
      quantityG: number;
      kcal: number;
      protein_g: number;
      carbs_g: number;
      fat_g: number;
      /** The pot's own food, so a re-weigh on Today rescales off the right one. */
      foodItemId: string | null;
      localDate: string;
    },
  ): string;
  quickAdd(input: {
    name: string;
    kcal: number;
    protein_g?: number;
    carbs_g?: number;
    fat_g?: number;
    meal?: Meal;
    localDate: string;
    /** Rarely set: `localDate` already answers "which day", and this is derived
     *  from it. Present so a caller that knows the exact minute can say so. */
    logged_at?: string;
  }): void;
  /**
   * Change an entry that has already been written.
   *
   * Takes only the two fields it uses, for the same reason `deleteLog` below
   * does: the add sheet corrects a weight it set moments ago and holds an id
   * and a day, not a refetched row.
   */
  updateLog(entry: Pick<FoodLog, "id" | "local_date">, changes: Partial<FoodLog>): void;
  /**
   * Remove an entry.
   *
   * Takes the two fields it uses rather than a whole `FoodLog`, so a caller
   * holding only what it just wrote — an id and the day it went on — can undo
   * it without first refetching the row to satisfy a type.
   */
  deleteLog(entry: Pick<FoodLog, "id" | "local_date">): void;
}

const OfflineContext = createContext<Offline | null>(null);

export interface OfflineProviderProps {
  api: ApiClient;
  /**
   * A zone to fall back on until the profile has loaded.
   *
   * The zone matters here for one thing only: turning "this belongs to Tuesday"
   * into the `logged_at` the API actually takes. Doing that in the provider
   * means every write is filed under the day its caller named, and there is one
   * place where that can be true or false rather than six.
   *
   * The real answer is the *profile's* zone, which is what the server buckets
   * days by — but this provider deliberately does not subscribe to the profile.
   * It mounts before anyone is known to be signed in, and a query here would
   * fire `/v1/me` at a launch that is on its way to the sign-in screen. So the
   * profile is read out of the cache at the moment of each write instead, which
   * is both cheaper and more current than a value captured at render. Pass the
   * device's zone; it is used only in the window before the profile arrives.
   */
  timeZone: string;
  /** Draining is pointless while signed out, and a 401 keeps writes queued
   *  rather than discarding somebody's diary — so a session coming back is one
   *  of the moments worth retrying on. */
  signedIn: boolean;
  storage: QueueStorage;
  /**
   * Subscribe to "the network situation may have changed". Returns an
   * unsubscribe. Called once; implementations should register every signal
   * their platform offers and call back on each.
   */
  onWake(wake: () => void): () => void;
  children: ReactNode;
}

export function OfflineProvider({
  api,
  timeZone,
  signedIn,
  storage,
  onWake,
  children,
}: OfflineProviderProps) {
  const client = useQueryClient();

  // The queue lives in a ref because the drain reads it from callbacks and
  // timers that must see the latest value, not the one captured at render.
  // `pending` and `pendingIds` mirror it for the UI.
  const queue = useRef<QueuedWrite[]>([]);
  const [pending, setPending] = useState(0);
  const [pendingIds, setPendingIds] = useState<ReadonlySet<string>>(() => new Set());
  const [rejected, setRejected] = useState(0);
  const draining = useRef(false);
  const attempts = useRef(0);
  /**
   * Local id → server id, for entries created on this device.
   *
   * Kept across drains, not just within one: a meal logged offline can sync in
   * one drain and be deleted before the diary has refetched, at which point the
   * only id the client holds for that row is still the local one.
   *
   * In memory only, and it can be. A local id only ever reaches the screen
   * through a create that is still queued — including one restored from a
   * previous launch, whose row is put back on the day by `applyQueued` — and
   * that create is queued ahead of anything naming it, so the id is resolved in
   * the same drain that will need it.
   */
  const resolvedIds = useRef<Record<string, string>>({});
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  /**
   * The zone to file a write under, read when the write happens.
   *
   * Out of the cache rather than off a prop, so a profile that loads after this
   * provider rendered still decides the day — and so a traveller whose zone
   * changed mid-session does not keep writing against the old one.
   */
  const zoneNow = useCallback(
    () => client.getQueryData<Profile>(keys.profile)?.timezone ?? timeZone,
    [client, timeZone],
  );

  const commit = useCallback(
    (next: QueuedWrite[]) => {
      queue.current = next;
      setPending(next.length);
      // Only creates carry an id a diary row can match: an update or a delete
      // names a row that is already on screen under the server's id.
      setPendingIds(new Set(next.filter((w) => w.op === "log" || w.op === "quick").map((w) => w.id)));
      void storage.save(next);
    },
    [storage],
  );

  const drain = useCallback(async () => {
    // Single-flight. Two drains at once would replay the same writes twice —
    // idempotent on the server thanks to `client_id`, but the second would also
    // resolve stale ids and PATCH rows the first had already moved on from.
    if (draining.current || !queue.current.length || !signedIn) return;
    draining.current = true;
    if (timer.current) {
      clearTimeout(timer.current);
      timer.current = null;
    }

    let progressed = false;
    let stalled = false;

    try {
      // One write per pass, re-reading the queue each time.
      //
      // The obvious shape — hand the whole array to `drainQueue` and merge the
      // result back — has a race with no good fix: a write made *during* the
      // drain collapses against a snapshot entry that has already been sent, so
      // deleting an entry mid-sync silently drops the delete and leaves the row
      // on the server. Removing each write as it lands means `enqueue` only
      // ever collapses against writes that genuinely have not gone yet.
      while (queue.current.length) {
        const head = queue.current[0]!;
        const result = await drainQueue(api, [head], resolvedIds.current);
        resolvedIds.current = result.resolved;

        if (result.remaining.length) {
          stalled = true;
          break;
        }
        if (result.dropped.length) setRejected((count) => count + result.dropped.length);
        progressed = true;

        // Removed by identity, so a write that merged into this one while it
        // was in flight stays queued and is sent again — a PATCH carrying the
        // fields it already sent plus the new one, which is exactly right.
        commit(queue.current.filter((write) => write !== head));
      }

      if (progressed) {
        // The optimistic rows carried local ids; the server's are the real
        // ones. Refetching is cheaper than reconciling, and it happens once per
        // drain rather than once per write.
        void client.invalidateQueries({ queryKey: ["day"] });
        void client.invalidateQueries({ queryKey: ["logs"] });
        void client.invalidateQueries({ queryKey: ["overview"] });

        // The two lists derived from the log — Recent, and the usuals of every
        // meal — are stale the moment anything is written. Marked stale rather
        // than refetched: the sheet that did the writing is still open on them,
        // and a list that reorders under the thumb between two taps is worse
        // than one a few seconds behind. The next time it opens, it is right.
        void client.invalidateQueries({ queryKey: keys.recent, refetchType: "none" });
        void client.invalidateQueries({ queryKey: ["food", "usuals"], refetchType: "none" });
        // And the weights each food is habitually logged at, for the same
        // reason and with the same restraint: the pad may be open on the very
        // food that just landed, and its chips must not reorder mid-reach.
        void client.invalidateQueries({ queryKey: ["food", "portions"], refetchType: "none" });
        // The fridge, if anything eaten out of one drained. Refetched rather
        // than left stale, unlike the two above: the count on a stored tile is
        // a number the user is about to act on again, and "2 left" beside a pot
        // holding one is the mistake that sends somebody to the kitchen.
        void client.invalidateQueries({ queryKey: keys.batches });
      }

      if (stalled) {
        const wait = RETRY_MS[Math.min(attempts.current, RETRY_MS.length - 1)]!;
        attempts.current += 1;
        timer.current = setTimeout(() => void drain(), wait);
      } else {
        attempts.current = 0;
      }
    } finally {
      draining.current = false;
    }
  }, [api, client, commit, signedIn]);

  /**
   * Put the queue back on a day the server has just answered for.
   *
   * A day is stale as soon as it is cached, so it is refetched constantly — on
   * focus, on remount, after every drain. The response is the server's truth
   * and does not contain writes that have not reached it, so without this the
   * entry the user logged a moment ago vanishes from the screen while sitting
   * safely in the queue. `applyQueued` decides what goes back; this decides
   * when.
   *
   * Guarded on `manual`, which is the flag `setQueryData` sets and a fetch does
   * not: replaying writes an updated day, which would otherwise be another
   * update to react to, forever.
   */
  const replay = useCallback(
    (date: string) => {
      const day = client.getQueryData<DaySummary>(keys.day(date));
      if (!day) return;
      const entries = applyQueued(day.entries, date, queue.current);
      // Unchanged means the queue has nothing to say about this day. Writing
      // anyway would re-render every screen watching it, once per refetch.
      if (entries === day.entries) return;
      patchDay(client, date, () => entries);
    },
    [client],
  );

  /** Every cached day, for when the queue changes rather than the day. */
  const replayAll = useCallback(() => {
    for (const query of client.getQueryCache().findAll({ queryKey: ["day"] })) {
      const [, date] = query.queryKey;
      if (typeof date === "string") replay(date);
    }
  }, [client, replay]);

  useEffect(() => {
    return client.getQueryCache().subscribe((event) => {
      if (event.type !== "updated") return;
      if (event.action.type !== "success" || event.action.manual) return;
      if (!queue.current.length) return;
      const [kind, date] = event.query.queryKey;
      if (kind !== "day" || typeof date !== "string") return;
      replay(date);
    });
  }, [client, replay]);

  // Load what a previous launch left behind, then try to clear it.
  useEffect(() => {
    let cancelled = false;
    void storage.load().then((stored) => {
      if (cancelled || !stored.length) return;
      queue.current = stored;
      setPending(stored.length);
      setPendingIds(
        new Set(stored.filter((w) => w.op === "log" || w.op === "quick").map((w) => w.id)),
      );
      // What the last launch could not send is food the user logged, and until
      // it sends it exists nowhere the server knows about. Whether it is on
      // screen comes down to which finished first, this load or the day's
      // fetch — so both put it back, and the second one finds nothing to do.
      replayAll();
      void drain();
    });
    return () => {
      cancelled = true;
    };
  }, [drain, replayAll, storage]);

  // Whatever this platform considers "the network may be back".
  useEffect(() => {
    return onWake(() => {
      attempts.current = 0;
      void drain();
    });
  }, [onWake, drain]);

  // A session that comes back after a stall is the other moment worth retrying:
  // `drainQueue` deliberately keeps a 401 in the queue rather than discarding a
  // diary, so the writes are still there waiting.
  useEffect(() => {
    if (signedIn) {
      attempts.current = 0;
      void drain();
    }
  }, [signedIn, drain]);

  useEffect(
    () => () => {
      if (timer.current) clearTimeout(timer.current);
    },
    [],
  );

  const push = useCallback(
    (write: QueuedWrite, optimistic?: () => void) => {
      commit(enqueue(queue.current, write));
      optimistic?.();
      void drain();
    },
    [commit, drain],
  );

  const value = useMemo<Offline>(
    () => ({
      pending,
      pendingIds,
      rejected,
      acknowledgeRejected: () => setRejected(0),
      retry: () => {
        attempts.current = 0;
        void drain();
      },

      logFood(input, hint) {
        // Minted here rather than in the hook, because this id is also the
        // optimistic row's id — an edit made before the queue drains has to
        // name the same entry.
        const clientId = globalThis.crypto.randomUUID();
        // The day the caller named, as the instant the API takes. Without it
        // the server files the entry under whenever the write arrives, which is
        // today — and on a screen showing last Tuesday that is the wrong day,
        // silently, with the row disappearing on the next refetch.
        const loggedAt = input.logged_at ?? instantOn(hint.localDate, zoneNow()).toISOString();
        // Built once and used twice: shown now, and kept with the write so the
        // same row can be put back on a day the server has just re-answered
        // without it. Two rows built from one set of figures would be two
        // chances to disagree.
        const row = optimisticEntry({
          clientId,
          localDate: hint.localDate,
          loggedAt,
          name: hint.name,
          meal: hint.meal,
          quantityG: hint.quantityG,
          macros: forGrams(hint.per100, hint.quantityG),
          nutrients: forGramsNutrients(hint.per100, hint.quantityG),
          foodItemId: input.food_item_id,
        });
        push(
          {
            op: "log",
            id: clientId,
            body: { ...input, logged_at: loggedAt, client_id: clientId },
            optimistic: row,
          },
          () => patchDay(client, hint.localDate, (entries) => [...entries, row]),
        );
        return clientId;
      },

      logPortion(input, hint) {
        const clientId = globalThis.crypto.randomUUID();
        const loggedAt = instantOn(hint.localDate, zoneNow()).toISOString();
        const row = optimisticEntry({
          clientId,
          localDate: hint.localDate,
          loggedAt,
          name: hint.name,
          meal: input.meal,
          quantityG: hint.quantityG,
          macros: {
            kcal: hint.kcal,
            protein_g: hint.protein_g,
            carbs_g: hint.carbs_g,
            fat_g: hint.fat_g,
          },
          // Left unknown rather than nought. The tile shows four figures and
          // the server will snapshot the rest from the pot's own food when this
          // lands; claiming a home-cooked pot has no fibre in it would write
          // that gap into the diary permanently.
          nutrients: NO_NUTRIENTS,
          foodItemId: hint.foodItemId,
        });
        push(
          {
            op: "portion",
            id: clientId,
            batchId: input.batchId,
            body: {
              client_id: clientId,
              meal: input.meal,
              // One measure on the wire. Grams when the plate was weighed —
              // the server derives the portions from them and the two agree
              // because both round the same way — portions otherwise.
              ...(input.quantity_g != null
                ? { quantity_g: input.quantity_g }
                : { portions: input.portions }),
              logged_at: loggedAt,
            },
            optimistic: row,
            spent: input.portions,
          },
          () => {
            patchDay(client, hint.localDate, (entries) => [...entries, row]);
            // And take it out of the fridge on screen, at once. The write is
            // queued, so the server's count does not move until it drains, and
            // a tile still reading "2 left" under a thumb that just ate one is
            // the reason the tap gets made twice.
            spendPortion(client, input.batchId, input.portions);
          },
        );
        return clientId;
      },

      quickAdd({ localDate, ...body }) {
        const clientId = globalThis.crypto.randomUUID();
        // Same reason as `logFood` above: `localDate` is the caller's answer to
        // "which day", and `logged_at` is the only way to say it to the API.
        const loggedAt = body.logged_at ?? instantOn(localDate, zoneNow()).toISOString();
        const row = optimisticEntry({
          clientId,
          localDate,
          loggedAt,
          name: body.name,
          meal: body.meal ?? "snack",
          // Nothing was weighed. The column is NOT NULL, so zero is the
          // honest value, and the macros are the figures as typed.
          quantityG: 0,
          macros: {
            kcal: body.kcal,
            protein_g: body.protein_g ?? 0,
            carbs_g: body.carbs_g ?? 0,
            fat_g: body.fat_g ?? 0,
          },
          // Left unknown, and note that this is the opposite call to the zero
          // above. Quantity is zero because the column cannot hold "unweighed"
          // and zero says it truthfully. These columns *can* hold "unknown",
          // and a quick add is exactly that — someone read a calorie figure off
          // a menu. A nought here would claim the meal had no sugar in it.
          nutrients: NO_NUTRIENTS,
          foodItemId: null,
        });
        push(
          {
            op: "quick",
            id: clientId,
            body: { ...body, logged_at: loggedAt, client_id: clientId },
            optimistic: row,
          },
          () => patchDay(client, localDate, (entries) => [...entries, row]),
        );
      },

      updateLog(entry, changes) {
        push({ op: "update", id: entry.id, changes }, () =>
          patchDay(client, entry.local_date, (entries) =>
            entries.map((row) => (row.id === entry.id ? { ...row, ...changes } : row)),
          ),
        );
      },

      deleteLog(entry) {
        /* Undoing a portion before the write has drained: the queue collapses
           the create and the delete into nothing at all, so the server never
           hears about either — and never puts the portion back, because it
           never took one out. The screen did, optimistically, so the screen has
           to undo that itself.

           Read before `push`, which is what collapses the pair. Once the create
           has drained there is no pending write to find, and the server's own
           restore reaches the cache through the invalidation after the delete
           sends. */
        const pending = queue.current.find(
          (write) => write.op === "portion" && write.id === entry.id,
        );
        push({ op: "delete", id: entry.id }, () => {
          patchDay(client, entry.local_date, (entries) =>
            entries.filter((row) => row.id !== entry.id),
          );
          if (pending?.op === "portion") {
            // `spent` is what the screen took; a write from before it existed
            // carries only the body's portions, and one with neither was one.
            spendPortion(client, pending.batchId, -(pending.spent ?? pending.body.portions ?? 1));
          }
        });
      },
    }),
    [pending, pendingIds, rejected, push, client, drain, zoneNow],
  );

  return <OfflineContext.Provider value={value}>{children}</OfflineContext.Provider>;
}

export function useOffline(): Offline {
  const offline = useContext(OfflineContext);
  if (!offline) throw new Error("useOffline must be used inside <OfflineProvider>");
  return offline;
}
